import getConfig from 'next/config';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useShallowEqualSelector } from '../../../../../hooks/redux';

import {
  CreateUserWithResvQuery,
  useAuth,
} from '../../../../../hooks/api/auth';
import { useCollectRwgConversion } from 'src/hooks/rwg';

import { SettingCompletion } from '../../../../../interfaces/merchants/resources/books';
import { setMeToBookInfo } from '../../../../../redux/actions/merchants/resources/books';

import BooksEventItemList from '../../../../../molecules/merchant/resources/book/BooksEventItemList';
import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksNotes from '../../../../../molecules/merchant/resources/book/BooksNotes';
import BooksSection from '../../../../../molecules/merchant/resources/book/BooksSection';
import BooksCompleteAlert from '../../../../../molecules/merchant/resources/book/BooksCompleteAlert';
import BooksRequestAlert from '../../../../../molecules/merchant/resources/book/BooksRequestAlert';
import BooksCenteringUnderLineMessage from '../../../../../molecules/merchant/resources/book/BooksCenteringUnderLineMessage';
import InputCompletionCreateUser from '../../../../../molecules/merchant/resources/book/InputCompletionCreateUser';

import * as gtag from '../../../../../utils/gtag';
import { useResourceNotes } from '../../../../../helpers/merchants/resources/helper';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';
import { useAppDispatch } from '../../../../../redux';
import { ErrorType } from '../../../../../domain/entities/Auth';
import { RSButtonAnchor } from 'src/reservation-page/RSButtonAnchor';
import { buildSiteUrl } from 'src/utils/request-url';

const { publicRuntimeConfig } = getConfig();
const isProduction = publicRuntimeConfig.coubicEnv === 'production';

interface ElementProps {
  merchantResource: { publicId: string; name: string; imageUrl?: string };
  settings: SettingCompletion;
  publicId: string;
  withExpressFlow: boolean;
  resourcePublicId: string;
}

type Props = ElementProps;

const InputCompletionEvent: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const { createUserWithResv } = useAuth();
  const [errors, setErrors] = useState<ErrorType[]>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [postable, setPostable] = useState(true);
  const { merchantResource, settings, withExpressFlow, resourcePublicId } =
    props;
  const intl = useIntl();
  const notes = useResourceNotes(settings);

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const continuousReservationQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('continuous_reservation', 'true');
    return params.toString();
  }, []);

  useCollectRwgConversion();

  useEffect(() => {
    const reservationId = settings.createUserWithResv!.reservationId;

    if (isProduction) {
      // Fire GTM booking_completion event
      gtag.bookingEvent({
        step: 'completion',
        reservationPublicId: reservationId,
        resourcePublicId: merchantResource.publicId,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateUser = useCallback(
    async (query: CreateUserWithResvQuery) => {
      setPostable(false);
      createUserWithResv(query)
        .then(async ({ errorType }) => {
          if (errorType) return setErrors([errorType]);
          await dispatch(setMeToBookInfo(inputs.merchantId!, resourcePublicId));
          setSubmitted(true);
        })
        .catch((_err) => {
          return;
        })
        .finally(() => setPostable(true));
    },
    [createUserWithResv, dispatch, inputs.merchantId, resourcePublicId],
  );

  const reservations = inputs;

  return (
    <div>
      {props.settings.finishedStatus === 'accepted' && <BooksCompleteAlert />}
      {props.settings.finishedStatus === 'pending' && <BooksRequestAlert />}
      <BooksSection>
        {settings.createUserWithResv!.available &&
          (submitted ? (
            <BooksCenteringUnderLineMessage>
              {intl.formatMessage({
                id: 'books.formsLabel.createUserSubmitted',
              })}
            </BooksCenteringUnderLineMessage>
          ) : (
            <InputCompletionCreateUser
              postable={postable}
              reservationId={settings.createUserWithResv!.reservationId}
              errors={errors}
              email={reservations.userInfo!.customerFields!.email}
              onSubmit={(query: CreateUserWithResvQuery) =>
                handleCreateUser(query)
              }
            />
          ))}
      </BooksSection>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.booksItems',
        })}
        paddingTop={true}
      >
        <BooksEventItemList
          nickname={props.publicId}
          resourceId={merchantResource.publicId}
          resourceName={merchantResource.name}
          resourceImage={merchantResource.imageUrl ?? ''}
          resourceUrl={buildSiteUrl(`/${merchantResource.publicId}`)}
          price={
            reservations.payment &&
            reservations.payment.paymentMethod.method !== 'subscription' &&
            reservations.payment.paymentMethod.method !== 'ticket' &&
            reservations.payment.paymentMethod.method !==
              'first_time_free_voucher'
              ? reservations.payment.paymentMethod.price
              : undefined
          }
          payment={
            reservations.payment && reservations.payment.paymentMethod.method
          }
          eventTimeSlot={reservations.eventTimeSlot!}
          attendees={reservations.eventTimeSlot!.attendees}
          googleCalendarTemplateUrl={
            reservations.googleCalendarTemplateUrl ?? ''
          }
        />
      </LabeledContainer>
      {!withExpressFlow ? (
        <>
          <BooksSection withPadding={true} rightSide={true}>
            <RSButtonAnchor variant="outline" isBlock href={buildSiteUrl()}>
              {intl.formatMessage({
                id: 'books.formsLabel.backToMainPage',
              })}
            </RSButtonAnchor>
          </BooksSection>
          <BooksSection withPadding={true} rightSide={true}>
            <RSButtonAnchor
              variant="outline"
              isBlock
              href={buildSiteUrl(
                `/${resourcePublicId}/book?${continuousReservationQuery}`,
              )}
            >
              {intl.formatMessage({
                id: 'books.formsLabel.continuousReservation',
              })}
            </RSButtonAnchor>
          </BooksSection>
        </>
      ) : (
        <BooksSection withPadding={true} rightSide={true}>
          <RSButtonAnchor
            variant="outline"
            isBlock
            href={buildSiteUrl(
              `/${resourcePublicId}/book?${continuousReservationQuery}`,
            )}
          >
            {intl.formatMessage({
              id: 'books.formsLabel.continuousReservation',
            })}
          </RSButtonAnchor>
        </BooksSection>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.attentionForBooking',
        })}
        contentsPadding={true}
        paddingTop={true}
      >
        <BooksNotes notes={notes} />
      </LabeledContainer>
    </div>
  );
};

export default InputCompletionEvent;
